import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import * as challengePageActions from 'pages/CommunitySelectedPage/actions';
import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import { ChallengeName, Wrapper, Logo, HeaderContainer, CenteringWrapper } from '../Hackathon/styles';
import {
  PixiAndMatchListContainer,
  MatchListContainer,
  MatchTable,
  TableTitle,
  TableRow,
  PlayerContainer,
  PlayerBackground,
  NoMatchesFoundContainer,
  InputBox,
} from './styles';
import { Button } from 'components/Button/RoundButton';
import { FreecodePlayer } from 'components/FreecodePlayer/freecodePlayer';
import { Images } from 'assets/images';
import { executorDecompress } from 'functions/executorDecompress';
import { Spinner } from 'components/Spinner/Spinner';
import { toast } from 'react-toastify';

export default function HackathonMatches() {
  const params = useParams();
  const id = params.id;
  const [matchesData, setMatchesData] = useState([]);
  const [matchIndex, setMatchIndex] = useState(0);
  const [levelToRun, setLevelToRun] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const { getChallenge } = useActions(challengePageActions);
  const { getTournamentMatches } = useActions(actions);

  const { selectedItem } = useSelector((state) => state.CommunitySelectedPageReducer);
  const { matches, loading, error } = useSelector((state) => state.HackathonMatchesReducer);

  useEffect(() => {
    if (!getChallenge || !id || !getTournamentMatches) return;
    getChallenge(id, 'challenges');
    getTournamentMatches(id);
  }, [getChallenge, id, getTournamentMatches]);

  useEffect(() => {
    const decompressPlayback = () => {
      try {
        let data = matches.map((match) => {
          return {
            participants: match.participants,
            levels: executorDecompress(match),
            finishedAt: match.finishedAt,
          };
        });
        setMatchesData(data);
      } catch (e) {
        console.log('Error on decompressing match data: ', e);
      }
    };
    if (matches.length) decompressPlayback();
  }, [matches]);

  useEffect(() => {
    if (error && !error.includes('internal error')) toast.error(error);
  }, [error]);

  const renderPixi = (index) => (
    <PlayerContainer>
      <PlayerBackground>
        <FreecodePlayer
          testImplementation={selectedItem.tournamentConfig}
          levels={matchesData[index].levels}
          levelToRun={levelToRun}
          setLevelToRun={setLevelToRun}
          playByDefault
        />
      </PlayerBackground>
    </PlayerContainer>
  );

  const renderMatchesList = () => {
    return matches.map((match, index) => {
      if (
        searchQuery &&
        !match.participants.some((participant) => participant.name.toLowerCase().includes(searchQuery.toLowerCase()))
      )
        return false;
      return (
        <TableRow key={index} isGrey={index % 2 === 0}>
          <TableTitle makeLeft className="col-6">
            {match.participants.map(
              (participant, i) => `${participant.name}${i !== match.participants.length - 1 ? ', ' : ''}`
            )}
          </TableTitle>
          <TableTitle className="col-3">{match.winner.name}</TableTitle>
          <TableTitle className="col-3">
            <Button
              primary
              primaryGreenBorder
              small
              disabled={index === matchIndex}
              onClick={() => setMatchIndex(index)}
            >
              {index === matchIndex ? 'Playing' : 'Play'}
            </Button>
          </TableTitle>
        </TableRow>
      );
    });
  };

  const renderTable = () => (
    <MatchListContainer>
      <MatchTable>
        <TableTitle isHead className="col-6">
          Participants
        </TableTitle>
        <TableTitle isHead className="col-3">
          Winner
        </TableTitle>
        <TableTitle isHead className="col-3">
          Match
        </TableTitle>
      </MatchTable>
      {renderMatchesList()}
    </MatchListContainer>
  );

  return (
    <CenteringWrapper>
      <Wrapper full column center padding>
        <HeaderContainer>
          <Logo src={Images.logoBlue} />
          <ChallengeName>{selectedItem.name}</ChallengeName>
          <Logo src={selectedItem?.landingPageLogo ?? ''} />
        </HeaderContainer>
        {loading && !matchesData.length && <Spinner />}
        {!loading && matchesData.length > 0 ? (
          <PixiAndMatchListContainer>
            {renderPixi(matchIndex)}
            <InputBox
              type="text"
              value={searchQuery}
              className="form-control mb-1"
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search by name"
            />
            {renderTable()}
          </PixiAndMatchListContainer>
        ) : (
          <NoMatchesFoundContainer>No matches found</NoMatchesFoundContainer>
        )}
      </Wrapper>
    </CenteringWrapper>
  );
}
